import React, { useState, useEffect, useRef } from 'react';
import '../styles/About.css';
import { motion, useInView } from 'framer-motion';
import SEO from '../components/SEO';

// Types
interface TeamMember {
  id: number;
  name: string;
  role: string;
  bio: string;
  imageUrl: string;
  credentials?: string;
  socialLinks?: { platform: string; url: string; icon: string }[];
}

interface CompanyValue {
  id: number;
  title: string;
  description: string;
  icon: string;
}

interface Benefit {
  id: number;
  title: string;
  description: string;
  icon: string;
}

interface FAQItem {
  id: number;
  question: string;
  answer: string;
}

const About: React.FC = () => {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  // Data
  const teamMembers: TeamMember[] = [
    {
      id: 1,
      name: "Kelly Buckley",
      role: "Physician Assistant",
      credentials: "PA-C",
      bio: "Kelly leads Vital Balance Hormones with a mission to provide education-first, deeply personalized hormone therapy care. With years of hands-on experience, Kelly is passionate about guiding women toward health and clarity through safe, empowering solutions.",
      imageUrl: "../assets/team-doctor.jpg",
      socialLinks: [
        { platform: "linkedin", url: "https://linkedin.com/in/kelly", icon: "linkedin" },
      ],
    }
  ];

  const companyValues: CompanyValue[] = [
    { id: 1, title: "Personalized Care", description: "Tailored treatments for your unique needs.", icon: "person-circle" },
    { id: 2, title: "Scientific Excellence", description: "Therapies based on cutting-edge research.", icon: "flask" },
    { id: 3, title: "Holistic Approach", description: "Restoring balance to your whole system.", icon: "balance-scale" },
    { id: 4, title: "Continuous Support", description: "Ongoing care throughout your journey.", icon: "heart-pulse" },
  ];

  const benefits: Benefit[] = [
    { id: 1, title: "Increased Energy", description: "Restore vitality and combat fatigue.", icon: "bolt" },
    { id: 2, title: "Improved Sleep", description: "Experience deeper, restorative sleep.", icon: "moon" },
    { id: 3, title: "Enhanced Mood", description: "Reduce mood swings and anxiety.", icon: "smile" },
    { id: 4, title: "Mental Clarity", description: "Improve focus and memory.", icon: "brain" },
    { id: 5, title: "Weight Management", description: "Support healthy metabolism.", icon: "weight" },
    { id: 6, title: "Stronger Bones", description: "Protect bone density.", icon: "bone" },
  ];

  const faqItems: FAQItem[] = [
    {
      id: 1,
      question: "What are bioidentical hormones?",
      answer: "Bioidentical hormones match your body's natural hormones, potentially reducing side effects.",
    },
    {
      id: 2,
      question: "How do I know if hormone therapy is right for me?",
      answer: "We conduct comprehensive testing to determine if hormone imbalances affect your symptoms.",
    },
    {
      id: 3,
      question: "Is bioidentical hormone therapy safe?",
      answer: "When monitored by professionals, it's safe for most women.",
    },
    {
      id: 4,
      question: "How long until I see results?",
      answer: "Many notice improvements within 2-3 weeks, with full benefits in 3-6 months.",
    },
    {
      id: 5,
      question: "What payment options are available?",
      answer: "We offer various payment plans to make treatment accessible.",
    },
  ];

  // About page specific schema
  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://vitalbalancehormones.com/about"
    },
    "headline": "About VitalBalanceHormones - Our Mission, Values, and Team",
    "description": "Learn about VitalBalanceHormones, our mission to empower women through personalized bioidentical hormone therapy, our core values, and meet our expert team.",
    "provider": {
      "@type": "MedicalOrganization",
      "name": "VitalBalanceHormones",
      "url": "https://vitalbalancehormones.com"
    },
    "employee": {
      "@type": "Person",
      "name": "Kelly Buckley",
      "jobTitle": "Physician Assistant",
      "description": "Kelly leads Vital Balance Hormones with a mission to provide education-first, deeply personalized hormone therapy care."
    }
  };

  // FAQ Toggle
  const toggleFaq = (id: number) => {
    setActiveFaq(activeFaq === id ? null : id);
  };

  // Animation Variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
  };

  return (
    <div className="about-page">
      <SEO 
        title="About Us | VitalBalanceHormones Bioidentical Hormone Therapy"
        description="Our mission is to empower women through personalized bioidentical hormone therapy. Meet our team of experts dedicated to restoring your hormonal balance and vitality."
        keywords="hormone therapy team, bioidentical hormones, women's wellness center, hormone specialists, Kelly Buckley PA-C, hormone therapy mission, hormone balance values"
        url="/about"
        schema={aboutSchema}
      />
      
      {/* Hero Section */}
      <section className="about-hero" role="banner" aria-label="About Hero">
        <div className="about-hero-content">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            Restoring Balance, Renewing Life
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.7 }}
            className="hero-subtitle"
          >
            Your trusted partner in bioidentical hormone therapy
          </motion.p>
          <motion.span
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.8, duration: 0.5 }}
            className="hero-badge"
          >
            Women's Wellness Specialists Since 2008
          </motion.span>
        </div>
      </section>

      {/* Mission Section */}
      <section className="about-container mission-section" aria-label="Our Mission">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <h2>Our Mission</h2>
          <div className="mission-content">
            <div className="mission-text">
              <p>
                At <span className="highlight-text">VitalBalanceHormones</span>, we are dedicated to empowering women through personalized bioidentical hormone therapies that restore balance, enhance vitality, and improve quality of life.
              </p>
              <p>
                Our approach combines cutting-edge medical science with compassionate care, addressing the full spectrum of your well-being—physical, emotional, and cognitive.
              </p>
              <p>
                We guide you with expertise and empathy, helping you reclaim a vibrant, balanced life.
              </p>
            </div>
            <figure className="mission-image-container">
              <img
                src="../assets/mission-image.jpg"
                alt="Women embracing wellness"
                className="mission-image"
                loading="lazy"
              />
            </figure>
          </div>
        </motion.div>
      </section>

      {/* Philosophy Section */}
      <section className="about-container philosophy-section" aria-label="Our Treatment Philosophy">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <div className="philosophy-content">
            <p className="philosophy-text highlight-paragraph">
              This is not just about relieving symptoms. It's about restoring balance, preventing long-term disease, and preserving quality of life with the safest, most natural approach available.
            </p>
            <p className="philosophy-text">
              At VitalBalance Hormones, we follow these principles — delivering personalized, organic-based care that supports not only how you feel today, but how you age tomorrow.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Values Section */}
      <section className="about-container values-section" aria-label="Our Core Values">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <h2>Our Core Values</h2>
          <div className="values-grid">
            {companyValues.map(value => (
              <motion.div key={value.id} className="value-card" variants={itemVariants}>
                <div className="value-icon">
                  <i className={`icon-${value.icon}`} aria-hidden="true"></i>
                </div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Benefits Section */}
      <section className="about-container benefits-section" aria-label="Benefits of Hormone Therapy">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <h2>Benefits of Hormone Therapy</h2>
          <p>Our treatments address a wide range of symptoms to improve your quality of life.</p>
          <div className="benefits-container">
            {benefits.map(benefit => (
              <motion.div
                key={benefit.id}
                className="benefit-item"
                variants={itemVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <div className="benefit-icon">
                  <i className={`icon-${benefit.icon}`} aria-hidden="true"></i>
                </div>
                <div className="benefit-text">
                  <h4>{benefit.title}</h4>
                  <p>{benefit.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Team Section */}
      <section className="about-container team-section" aria-label="Meet Kelly">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <h2>Meet Kelly</h2>
          <p className="team-intro">
            Your dedicated provider for hormonal wellness with expertise and compassion.
          </p>
          <div className="team-grid">
            {teamMembers.map(member => (
              <motion.div key={member.id} className="team-member-card" variants={itemVariants}>
                <div className="member-image-container">
                  <img
                    src={member.imageUrl}
                    alt={`${member.name}, ${member.role}`}
                    className="member-image"
                    loading="lazy"
                    onError={e => (e.currentTarget.src = "../assets/placeholder.jpg")}
                  />
                  <div className="member-social">
                    {member.socialLinks?.map((link, index) => (
                      <a
                        key={index}
                        href={link.url}
                        className="social-icon"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${link.platform} profile for ${member.name}`}
                      >
                        <i className={`icon-${link.icon}`} aria-hidden="true"></i>
                      </a>
                    ))}
                  </div>
                </div>
                <h3>{member.name}</h3>
                <h4>{member.role}</h4>
                {member.credentials && <p className="credentials">{member.credentials}</p>}
                <p>{member.bio}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Approach Section */}
      <section className="about-container approach-section" aria-label="Our Approach">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <h2>Our Approach to Hormone Therapy</h2>
          <div className="approach-steps">
            <div className="approach-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Comprehensive Assessment</h3>
                <p>We evaluate your hormonal profile and health history.</p>
              </div>
            </div>
            <div className="approach-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>Personalized Treatment Plan</h3>
                <p>A custom regimen designed for your body.</p>
              </div>
            </div>
            <div className="approach-step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Ongoing Monitoring</h3>
                <p>We track progress and adjust as needed.</p>
              </div>
            </div>
            <div className="approach-step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h3>Lifestyle Integration</h3>
                <p>Guidance to enhance overall wellness.</p>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* FAQ Section */}
      <section className="about-container faq-section" aria-label="Frequently Asked Questions">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <h2>Frequently Asked Questions</h2>
          <div className="faq-container">
            {faqItems.map(item => (
              <div
                key={item.id}
                className={`faq-item ${activeFaq === item.id ? 'active' : ''}`}
                aria-expanded={activeFaq === item.id}
              >
                <div
                  className="faq-question"
                  onClick={() => toggleFaq(item.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => e.key === 'Enter' && toggleFaq(item.id)}
                  aria-controls={`faq-answer-${item.id}`}
                >
                  <span>{item.question}</span>
                  <i className="icon-chevron-down" aria-hidden="true"></i>
                </div>
                <div id={`faq-answer-${item.id}`} className="faq-answer">
                  <p>{item.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* SEO Boost Section - Hormone Therapy Benefits & Conditions */}
      <section className="about-container seo-boost-section" aria-label="Hormone Therapy Benefits and Conditions">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <div className="seo-content-grid">
            {/* Conditions We Treat */}
            <div className="seo-content-block">
              <h2>Hormone Imbalance Conditions We Treat</h2>
              <p className="section-intro">
                Our bioidentical hormone replacement therapy (BHRT) effectively addresses a wide range of hormonal conditions affecting women's health and quality of life.
              </p>
              <div className="conditions-grid">
                <div className="condition-item">
                  <h3>Menopause & Perimenopause</h3>
                  <p>Comprehensive treatment for hot flashes, night sweats, mood changes, and sleep disturbances during menopause transition.</p>
                </div>
                <div className="condition-item">
                  <h3>Estrogen Deficiency</h3>
                  <p>Bioidentical estrogen therapy to restore optimal levels and alleviate symptoms like vaginal dryness and bone loss.</p>
                </div>
                <div className="condition-item">
                  <h3>Progesterone Imbalance</h3>
                  <p>Natural progesterone supplementation for irregular cycles, PMS symptoms, and hormonal mood disorders.</p>
                </div>
                <div className="condition-item">
                  <h3>Low Testosterone in Women</h3>
                  <p>Testosterone replacement therapy to improve energy, libido, muscle mass, and cognitive function in women.</p>
                </div>
                <div className="condition-item">
                  <h3>Thyroid Disorders</h3>
                  <p>Comprehensive thyroid hormone optimization including T3, T4, and reverse T3 testing and treatment.</p>
                </div>
                <div className="condition-item">
                  <h3>Adrenal Fatigue</h3>
                  <p>DHEA and cortisol support to restore healthy stress response and energy levels.</p>
                </div>
              </div>
            </div>

            {/* Benefits of BHRT */}
            <div className="seo-content-block">
              <h2>Benefits of Bioidentical Hormone Replacement Therapy</h2>
              <p className="section-intro">
                Experience the life-changing benefits of personalized bioidentical hormone therapy designed specifically for your body's unique needs.
              </p>
              <div className="benefits-list">
                <div className="benefit-category">
                  <h3>Physical Health Benefits</h3>
                  <ul>
                    <li>Improved energy levels and reduced fatigue</li>
                    <li>Better sleep quality and duration</li>
                    <li>Enhanced metabolism and weight management</li>
                    <li>Stronger bones and reduced osteoporosis risk</li>
                    <li>Healthier skin, hair, and nail quality</li>
                    <li>Reduced hot flashes and night sweats</li>
                  </ul>
                </div>
                <div className="benefit-category">
                  <h3>Mental & Emotional Wellness</h3>
                  <ul>
                    <li>Improved mood stability and reduced anxiety</li>
                    <li>Enhanced mental clarity and focus</li>
                    <li>Better stress management and resilience</li>
                    <li>Increased confidence and self-esteem</li>
                    <li>Reduced depression and mood swings</li>
                    <li>Improved cognitive function and memory</li>
                  </ul>
                </div>
                <div className="benefit-category">
                  <h3>Sexual & Reproductive Health</h3>
                  <ul>
                    <li>Restored libido and sexual desire</li>
                    <li>Improved vaginal health and lubrication</li>
                    <li>Enhanced sexual satisfaction and comfort</li>
                    <li>Balanced menstrual cycles</li>
                    <li>Reduced PMS and PMDD symptoms</li>
                    <li>Better reproductive health overall</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Why Choose BHRT */}
            <div className="seo-content-block">
              <h2>Why Bioidentical Hormones?</h2>
              <p className="section-intro">
                Hormones are essential to nearly every function in your body — from how you sleep and think, to how you burn fat, build muscle, and experience joy. When these hormones decline, you don't just "age" — you start to feel like a different version of yourself.
              </p>
              <div className="comparison-grid">
                <div className="comparison-item">
                  <h3>Molecular Structure</h3>
                  <p>Bioidentical hormones are chemically identical to hormones naturally produced by your body, ensuring optimal compatibility and reduced side effects.</p>
                </div>
                <div className="comparison-item">
                  <h3>Personalized Dosing</h3>
                  <p>Custom-compounded formulations allow for precise dosing tailored to your individual hormone levels and symptoms.</p>
                </div>
                <div className="comparison-item">
                  <h3>Natural Metabolism</h3>
                  <p>Your body recognizes and processes bioidentical hormones naturally, leading to more predictable and stable hormone levels.</p>
                </div>
                <div className="comparison-item">
                  <h3>Fewer Side Effects</h3>
                  <p>The identical molecular structure reduces the risk of adverse reactions commonly associated with synthetic hormones.</p>
                </div>
              </div>
            </div>

            {/* Treatment Approach */}
            <div className="seo-content-block">
              <h2>Our Evidence-Based Treatment Approach</h2>
              <p className="section-intro">
                VitalBalanceHormones follows the latest research and clinical guidelines to provide safe, effective hormone therapy treatments.
              </p>
              <div className="approach-highlights">
                <div className="highlight-item">
                  <h3>Comprehensive Testing</h3>
                  <p>Advanced hormone panels including saliva, blood, and urine testing to assess your complete hormonal profile and identify imbalances.</p>
                </div>
                <div className="highlight-item">
                  <h3>Individualized Protocols</h3>
                  <p>Treatment plans customized based on your symptoms, lab results, medical history, and personal health goals.</p>
                </div>
                <div className="highlight-item">
                  <h3>Ongoing Monitoring</h3>
                  <p>Regular follow-up appointments and lab work to track progress and adjust treatment for optimal results and safety.</p>
                </div>
                <div className="highlight-item">
                  <h3>Integrative Care</h3>
                  <p>Holistic approach combining hormone therapy with nutrition counseling, lifestyle modifications, and stress management techniques.</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default About;